
import {
  defineComponent, onMounted, watch, computed, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import {
  IData, IList, IMedia, IMixedData, IResponse,
} from '@/types/interfaces';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import ItemPhoto from '@/modules/drive/components/items/ItemPhoto.vue';
import ItemVideo from '@/modules/drive/components/items/ItemVideo.vue';
import { useDriveStore } from '../store/driveStore';
import { useMainStore } from '@/store/mainStore';
import { MEDIA_TYPES } from '@/types/enums';
import AppSkeletonMedia from '@/components/skeletons/AppSkeletonMedia.vue';
import SkeletonService from '@/services/SkeletonService';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import DriveService from '../services/DriveService';
import MediaService from '@/services/MediaService';
import 'swiper/swiper-bundle.css';

export default defineComponent({
  components: {
    AppItemWrapper,
    ItemPhoto,
    ItemVideo,
    AppSkeletonMedia,
    Swiper,
    SwiperSlide,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const dataLoading = ref(false);

    const { photoSkeletons } = SkeletonService.getSkeletonInfo();

    const isNetwork = computed(() => route.meta.isNetwork);
    const photosLastPage = computed(() => (isNetwork.value ? driveStore.networkPhotos.lastPage : driveStore.photos.lastPage));
    const photosCurrentPage = computed(() => (isNetwork.value ? driveStore.networkPhotos.page : driveStore.photos.page));

    const searchPhotos = ref<IMedia[]>([]);

    const photos = computed(() => {
      if (driveStore.search) {
        return searchPhotos.value.length > 50 ? searchPhotos.value.slice(0, 50) : searchPhotos.value;
      }
      const data = isNetwork.value ? driveStore.networkPhotos.data : driveStore.photos.data;
      return data.length > 50 ? data.slice(0, 50) : data;
    });

    const {
      scroller, pageIdx, isPageLoading, onScroll, onCacheScroll, setLastPage,
    } = UsePagination();

    function selectPhoto(photo: IMedia) {
      // driveStore.fullscreenPhoto = { ...photo, isMixed: false } as IMedia;
      driveStore.appPhotoViewMedia = { ...photo, isMixed: false } as IMedia;
    }

    async function fetchCacheData() {
      const resolveRequest = () => {
        if (isNetwork.value) {
          return driveStore.fetchNetworkPhotos();
        }
        return driveStore.fetchPhotos();
      };

      return resolveRequest()
        .then((response) => {
          driveStore.photoList = photos.value;
          return response;
        });
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = async () => {
        if (isNetwork.value) {
          return DriveService.getNetworkPhotos(pageIdx.value, driveStore.search)
            .then((res) => {
              const photosResponse: any = { ...res };
              photosResponse.results.data.items = photosResponse.results.data.items.map((item: IMixedData) => ({ ...item.file as IMedia, sharedTime: item.shared }));
              return photosResponse as IResponse<IData<IList<IMedia>>>;
            });
        }
        return MediaService.getMediaByType([MEDIA_TYPES.PHOTO, MEDIA_TYPES.VIDEO], pageIdx.value, driveStore.search);
      };
      return resolveRequest().then((response) => {
        if (currentSearch !== driveStore.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (pageIdx.value === 0) {
          searchPhotos.value = response.results.data.items;
        } else {
          searchPhotos.value.push(...response.results.data.items);
        }
        driveStore.photoList = photos.value;
        return response;
      });
    }

    function handleScroll(lastSlide?: boolean) {
      if (driveStore.search) {
        if (pageIdx.value >= 2) {
          return;
        }
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        if (photosCurrentPage.value >= 2) {
          return;
        }
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, photosLastPage.value, photosCurrentPage.value, false, lastSlide);
      }
    }

    function viewAllPhotos() {
      if (isNetwork.value) {
        router.push('/drive/network/photos');
      } else {
        router.push('/drive/my/photos');
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      driveStore.dataLoading.photos = true;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
            driveStore.dataLoading.photos = false;
          });
      }, 1000);
    });

    onMounted(() => {
      driveStore.photoList = photos.value;
    });

    return {
      mainStore,
      photoSkeletons,
      scroller,
      handleScroll,
      viewAllPhotos,
      isPageLoading,
      selectPhoto,
      MEDIA_TYPES,
      photos,
      dataLoading,
      isNetwork,
    };
  },
});
